import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import apiClient from '../services/api';
import {
  User,
  MapPin,
  Calendar,
  Award,
  GraduationCap,
  BadgeCheck,
  Shield,
  Mail,
  Phone,
  ExternalLink,
  Twitter,
  Linkedin,
  Facebook,
  Instagram,
  Youtube,
  Globe,
  Github,
  Heart,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowLeft,
  FileText,
  Briefcase,
  Languages,
  FolderHeart,
} from 'lucide-react';

const PublicProfilePage = () => {
  const { t } = useLanguage();
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('about');

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const [profileRes, campaignsRes] = await Promise.all([
        apiClient.get(`/profile/${userId}`),
        apiClient.get(`/campaigns?creator=${userId}&limit=10`).catch(() => ({ data: { campaigns: [] } })),
      ]);
      setProfile(profileRes.data);
      setCampaigns(campaignsRes.data.campaigns || campaignsRes.data || []);
    } catch (err) {
      setError('Profile not found');
      console.error('Profile fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const socialIcons = {
    twitter: Twitter,
    linkedin: Linkedin,
    facebook: Facebook,
    instagram: Instagram,
    youtube: Youtube,
    website: Globe,
    github: Github,
  };

  const getSocialUrl = (platform, handle) => {
    if (!handle) return null;
    if (handle.startsWith('http')) return handle;
    const prefixes = {
      twitter: 'https://twitter.com/',
      linkedin: 'https://linkedin.com/in/',
      facebook: 'https://facebook.com/',
      instagram: 'https://instagram.com/',
      youtube: 'https://youtube.com/@',
      github: 'https://github.com/',
      website: '',
    };
    return prefixes[platform] + handle;
  };

  const getVerificationCount = () => {
    if (!profile?.verification_status) return 0;
    const vs = profile.verification_status;
    return [vs.email_verified, vs.phone_verified, vs.kyc_verified, vs.identity_verified].filter(Boolean).length;
  };

  // Count items for tab badges
  const getCounts = () => ({
    education: profile?.education?.length || 0,
    certifications: profile?.certifications?.length || 0,
    achievements: profile?.achievements?.length || 0,
    skills: (profile?.skills?.length || 0) + (profile?.languages?.length || 0),
    campaigns: campaigns?.length || 0,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <User className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Profile Not Found</h1>
          <p className="text-muted-foreground mb-6">This user profile doesn't exist or has been removed.</p>
          <Link to="/">
            <Button><ArrowLeft className="w-4 h-4 mr-2" /> Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const counts = getCounts();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Button variant="ghost" onClick={() => window.history.back()} className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          {/* Profile Header Card */}
          <Card className="mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500/20 via-teal-500/20 to-emerald-500/20 h-24" />
            <CardContent className="relative pt-0">
              <div className="flex flex-col md:flex-row gap-6 -mt-12">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <img
                    src={profile.avatar || `https://ui-avatars.com/api/?name=${profile.name}&background=10b981&color=fff&size=150`}
                    alt={profile.name}
                    className="w-28 h-28 rounded-2xl border-4 border-background shadow-lg object-cover"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 pt-16 md:pt-4">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h1 className="text-2xl font-bold text-foreground">{profile.name}</h1>
                    {profile.member_id && (
                      <Badge className="bg-emerald-500/20 text-emerald-500 font-mono">
                        ID: {profile.member_id}
                      </Badge>
                    )}
                    {profile.trust_score >= 80 && (
                      <Badge className="bg-emerald-500/20 text-emerald-500">
                        <BadgeCheck className="w-3 h-3 mr-1" /> Trusted
                      </Badge>
                    )}
                    {getVerificationCount() >= 3 && (
                      <Badge className="bg-blue-500/20 text-blue-500">
                        <Shield className="w-3 h-3 mr-1" /> Verified
                      </Badge>
                    )}
                  </div>

                  {profile.headline && (
                    <p className="text-muted-foreground mb-2">{profile.headline}</p>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-3">
                    {profile.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> {profile.location}
                      </span>
                    )}
                    {profile.member_since && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" /> Member since {new Date(profile.member_since).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </div>

                  {/* Social Links */}
                  {profile.social_links && Object.keys(profile.social_links).some(k => profile.social_links[k]) && (
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(profile.social_links).map(([platform, handle]) => {
                        if (!handle) return null;
                        const Icon = socialIcons[platform] || Globe;
                        const url = getSocialUrl(platform, handle);
                        return (
                          <a
                            key={platform}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg bg-secondary hover:bg-emerald-500/20 transition-colors"
                            title={platform.charAt(0).toUpperCase() + platform.slice(1)}
                          >
                            <Icon className="w-4 h-4 text-foreground" />
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Stats - Clickable */}
                <div className="flex md:flex-col gap-6 md:gap-3 md:text-right pt-16 md:pt-4">
                  <button
                    onClick={() => setActiveTab('campaigns')}
                    className="text-center md:text-right hover:opacity-80 transition-opacity cursor-pointer"
                  >
                    <p className="text-2xl font-bold text-emerald-500">{profile.campaigns_created || 0}</p>
                    <p className="text-xs text-muted-foreground">Campaigns</p>
                  </button>
                  <button
                    onClick={() => setActiveTab('campaigns')}
                    className="text-center md:text-right hover:opacity-80 transition-opacity cursor-pointer"
                  >
                    <p className="text-2xl font-bold text-emerald-500">${(profile.total_raised || 0).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Total Raised</p>
                  </button>
                  {profile.trust_score > 0 && (
                    <div className="text-center md:text-right">
                      <p className="text-2xl font-bold text-teal-500">{profile.trust_score}%</p>
                      <p className="text-xs text-muted-foreground">Trust Score</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs Section */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full flex justify-start gap-1 mb-6 bg-secondary/50 p-1 rounded-xl overflow-x-auto border-0 scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <TabsTrigger value="about" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white rounded-lg whitespace-nowrap flex-shrink-0 border-0 shadow-none">
                <User className="w-4 h-4 mr-2" /> About
              </TabsTrigger>
              <TabsTrigger value="campaigns" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white rounded-lg whitespace-nowrap flex-shrink-0 border-0 shadow-none">
                <FolderHeart className="w-4 h-4 mr-2" /> Campaigns
                {counts.campaigns > 0 && <Badge className="ml-2 bg-rose-500/20 text-rose-400 text-xs">{counts.campaigns}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="education" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white rounded-lg whitespace-nowrap flex-shrink-0 border-0 shadow-none">
                <GraduationCap className="w-4 h-4 mr-2" /> Education
                {counts.education > 0 && <Badge className="ml-2 bg-blue-500/20 text-blue-400 text-xs">{counts.education}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="certifications" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white rounded-lg whitespace-nowrap flex-shrink-0 border-0 shadow-none">
                <FileText className="w-4 h-4 mr-2" /> Certifications
                {counts.certifications > 0 && <Badge className="ml-2 bg-emerald-500/20 text-emerald-400 text-xs">{counts.certifications}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="achievements" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white rounded-lg whitespace-nowrap flex-shrink-0 border-0 shadow-none">
                <Award className="w-4 h-4 mr-2" /> Achievements
                {counts.achievements > 0 && <Badge className="ml-2 bg-amber-500/20 text-amber-400 text-xs">{counts.achievements}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="skills" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white rounded-lg whitespace-nowrap flex-shrink-0 border-0 shadow-none">
                <Briefcase className="w-4 h-4 mr-2" /> Skills & Languages
                {counts.skills > 0 && <Badge className="ml-2 bg-teal-500/20 text-teal-400 text-xs">{counts.skills}</Badge>}
              </TabsTrigger>
            </TabsList>

            {/* About Tab */}
            <TabsContent value="about">
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <User className="w-5 h-5 text-emerald-500" /> About
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {profile.bio ? (
                        <p className="text-muted-foreground whitespace-pre-line">{profile.bio}</p>
                      ) : (
                        <p className="text-muted-foreground italic">No bio provided yet.</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
                
                {/* Verification Sidebar */}
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Shield className="w-5 h-5 text-emerald-500" /> Verification Status
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {profile.verification_status && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground flex items-center gap-2">
                              <Mail className="w-4 h-4" /> Email
                            </span>
                            {profile.verification_status.email_verified ? (
                              <CheckCircle className="w-5 h-5 text-emerald-500" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-500" />
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground flex items-center gap-2">
                              <Phone className="w-4 h-4" /> Phone
                            </span>
                            {profile.verification_status.phone_verified ? (
                              <CheckCircle className="w-5 h-5 text-emerald-500" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-500" />
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground flex items-center gap-2">
                              <Shield className="w-4 h-4" /> KYC
                            </span>
                            {profile.verification_status.kyc_verified ? (
                              <CheckCircle className="w-5 h-5 text-emerald-500" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-500" />
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground flex items-center gap-2">
                              <BadgeCheck className="w-4 h-4" /> Identity
                            </span>
                            {profile.verification_status.identity_verified ? (
                              <CheckCircle className="w-5 h-5 text-emerald-500" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-500" />
                            )}
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  {/* Trust Indicators */}
                  <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <Shield className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
                        <h4 className="font-semibold text-foreground mb-2">Why Trust This Creator?</h4>
                        <ul className="text-sm text-muted-foreground space-y-2 text-left">
                          {getVerificationCount() >= 2 && (
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                              Multiple verifications completed
                            </li>
                          )}
                          {profile.campaigns_created > 0 && (
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                              {profile.campaigns_created} campaign{profile.campaigns_created > 1 ? 's' : ''} created
                            </li>
                          )}
                          {profile.total_raised > 0 && (
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                              ${profile.total_raised.toLocaleString()} successfully raised
                            </li>
                          )}
                          {profile.social_links && Object.values(profile.social_links).some(v => v) && (
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                              Social media profiles linked
                            </li>
                          )}
                          {(profile.education?.length > 0 || profile.certifications?.length > 0) && (
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                              Education/certifications provided
                            </li>
                          )}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* Campaigns Tab */}
            <TabsContent value="campaigns">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Heart className="w-5 h-5 text-rose-500" /> Campaigns by {profile.name?.split(' ')[0]}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {campaigns.length > 0 ? (
                    <div className="grid md:grid-cols-2 gap-4">
                      {campaigns.map((campaign) => (
                        <Link
                          key={campaign.id}
                          to={`/campaign/${campaign.id}`}
                          className="flex gap-4 p-4 bg-secondary/50 rounded-xl hover:bg-secondary transition-colors"
                        >
                          <img
                            src={campaign.image || campaign.images?.[0] || 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=100&h=100&fit=crop'}
                            alt={campaign.title}
                            className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-foreground line-clamp-1">{campaign.title}</h4>
                            <p className="text-sm text-muted-foreground line-clamp-2">{campaign.description}</p>
                            <div className="flex items-center gap-4 mt-2">
                              <span className="text-sm text-emerald-500 font-medium">
                                ${(campaign.current_amount || campaign.raised || 0).toLocaleString()} raised
                              </span>
                              <span className="text-sm text-muted-foreground">
                                of ${(campaign.goal || 0).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FolderHeart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No campaigns created yet.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Education Tab */}
            <TabsContent value="education">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <GraduationCap className="w-5 h-5 text-blue-500" /> Education
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {profile.education && profile.education.length > 0 ? (
                    <div className="space-y-4">
                      {profile.education.map((edu, idx) => (
                        <div key={edu.id || idx} className="flex gap-4 p-4 bg-secondary/50 rounded-xl">
                          <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                            <GraduationCap className="w-7 h-7 text-blue-500" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-foreground text-lg">{edu.degree}</h4>
                            <p className="text-emerald-500 font-medium">{edu.institution}</p>
                            {edu.field && <p className="text-muted-foreground">{edu.field}</p>}
                            {(edu.start_year || edu.end_year) && (
                              <p className="text-sm text-muted-foreground mt-1">
                                📅 {edu.start_year} - {edu.end_year || 'Present'}
                              </p>
                            )}
                            {edu.description && (
                              <p className="text-sm text-muted-foreground mt-2 italic">{edu.description}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No education information provided yet.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Certifications Tab */}
            <TabsContent value="certifications">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="w-5 h-5 text-emerald-500" /> Certifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {profile.certifications && profile.certifications.length > 0 ? (
                    <div className="grid md:grid-cols-2 gap-4">
                      {profile.certifications.map((cert, idx) => (
                        <div key={cert.id || idx} className="flex gap-4 p-4 bg-secondary/50 rounded-xl">
                          <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                            <BadgeCheck className="w-6 h-6 text-emerald-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-foreground">{cert.name}</h4>
                            <p className="text-muted-foreground text-sm">{cert.issuer}</p>
                            {cert.date && <p className="text-xs text-muted-foreground mt-1">📅 {cert.date}</p>}
                            {cert.credential_id && (
                              <p className="text-xs text-muted-foreground">ID: {cert.credential_id}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No certifications provided yet.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Achievements Tab */}
            <TabsContent value="achievements">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Award className="w-5 h-5 text-amber-500" /> Achievements & Accolades
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {profile.achievements && profile.achievements.length > 0 ? (
                    <div className="space-y-4">
                      {profile.achievements.map((ach, idx) => (
                        <div key={ach.id || idx} className="flex gap-4 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/20">
                          <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Award className="w-7 h-7 text-amber-500" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-foreground text-lg">{ach.title}</h4>
                              {ach.date && (
                                <Badge className="bg-amber-500/20 text-amber-500">{ach.date}</Badge>
                              )}
                            </div>
                            {ach.description && (
                              <p className="text-muted-foreground mt-1">{ach.description}</p>
                            )}
                            {ach.url && (
                              <a
                                href={ach.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-emerald-500 hover:text-emerald-400 mt-2"
                              >
                                <ExternalLink className="w-4 h-4" /> View Verification
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Award className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No achievements provided yet.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Skills & Languages Tab */}
            <TabsContent value="skills">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Skills */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Briefcase className="w-5 h-5 text-teal-500" /> Skills
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {profile.skills && profile.skills.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {profile.skills.map((skill, idx) => (
                          <Badge key={idx} className="bg-teal-500/10 text-teal-400 px-3 py-1.5 text-sm">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        <Briefcase className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>No skills provided yet.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Languages */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Languages className="w-5 h-5 text-orange-500" /> Languages
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {profile.languages && profile.languages.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {profile.languages.map((lang, idx) => (
                          <Badge key={idx} className="bg-orange-500/10 text-orange-400 px-3 py-1.5 text-sm">
                            🌐 {lang}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        <Languages className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>No languages provided yet.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PublicProfilePage;
